import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import Modal from 'react-modal';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { rem } from 'polished';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleX } from '@fortawesome/pro-light-svg-icons';
import { Text } from '../Text';

Modal.setAppElement(`#___gatsby`);

type StyledSimpleModalProps = {
  imageWidth: string;
  imageHeight: string;
  stretch: boolean;
  center: boolean;
};

const StyledSimpleModal = styled.div<StyledSimpleModalProps>`
  border-radius: ${rem(`12px`)};
  max-width: ${rem(`450px`)};
  display: flex;
  flex: 1 0 100%;
  flex-wrap: wrap;
  padding: 1px;
  margin: 1px;
  border-radius: ${rem(`12px`)};
  position: relative;
  box-sizing: border-box;
  background-clip: padding-box;
  border: solid 1px transparent;
  transition: all 0.5s ease;
  background: ${({ theme }) => `linear-gradient(
        265deg,
        ${theme.colors.blue_1_65},
        ${theme.colors.blue_1},
        ${theme.colors.blue_1_90}
      )`};
  ::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: inherit;
    margin: -2px;
    z-index: -1;
    background: ${({ theme }) => `linear-gradient(
            -30deg,
            ${theme.colors.blue_1_30},
            ${theme.colors.blue_1_65},
            ${theme.colors.pink_2}
          )`};
  }
  .container {
    width: 100%;
    padding: ${rem(`24px`)};
  }
  .close-btn {
    position: absolute;
    top: -16px;
    right: -16px;
    color: ${({ theme }) => theme.colors.yellow_2};
    transition: color 300ms ease-in;
    :hover {
      color: ${({ theme }) => theme.colors.pink_2};
    }
  }
  .heading {
    display: flex;
    flex: 1 0 auto;
    flex-wrap: wrap;
    position: relative;
  }
  .image {
    display: flex;
    flex: ${({ stretch }) => (stretch ? `1 0 100%` : `0 1 auto`)};
    border-radius: ${rem(`6px`)};
    overflow: hidden;
    width: ${({ imageWidth }) => rem(imageWidth)};
    height: ${({ imageHeight }) => rem(imageHeight)};
    justify-content: ${({ center }) => (center ? `center` : `flex-start`)};
    margin-bottom: ${rem(`12px`)};
    margin-right: ${rem(`12px`)};
  }
  .image__item {
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .title {
    display: flex;
    flex: 1 0 auto;
    color: ${({ theme }) => theme.colors.white_1};
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    margin-bottom: ${rem(`12px`)};
    span {
      line-height: ${rem(`24px`)};
    }
  }
  .right-side {
    display: flex;
    flex: 0 1 auto;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding-left: ${rem(`6px`)};
    margin-bottom: ${rem(`12px`)};
  }
  .right-side__item {
    display: flex;
    align-items: center;
    margin-bottom: ${rem(`2px`)};
    span {
      color: white;
      margin-left: ${rem(`6px`)};
    }
  }
  .content {
    margin-bottom: ${rem(`12px`)};
  }
  .footer {
    display: flex;
    justify-content: center;
    a {
      display: flex;
    }
    span:first-child {
      margin-right: ${rem(`6px`)};
    }
  }
`;

export interface ISimpleModal {
  className?: string;
  onClose: () => void;
  isOpen: boolean;
  image?: {
    title: string;
    gatsbyImageData?: IGatsbyImageData;
    svg?: { originalContent: string };
  };
  title: string;
  rightSide?: ReactElement[];
  content: string;
  footer?: ReactElement;
  imageWidth?: string;
  imageHeight?: string;
  stretch?: boolean;
  center?: boolean;
}

export const SimpleModal = ({
  className = `default-modal`,
  onClose,
  isOpen,
  image,
  title,
  rightSide = [<></>],
  content,
  footer,
  imageWidth = `60px`,
  imageHeight = `60px`,
  stretch = false,
  center = false,
}: ISimpleModal) => {
  const clonedRightSide = rightSide.map((item) =>
    React.cloneElement(item, {
      key: uuidv4(),
      className: `right-side__item`,
    }),
  );
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={`simple-modal ${className}`}
      overlayClassName={`simple-modal-overlay ${className}-overlay`}
    >
      <StyledSimpleModal
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        stretch={stretch}
        center={center}
        className={`${className}-wrapper`}
      >
        <div className="container">
          <div className="close-btn">
            <button type="button" onClick={onClose}>
              <FontAwesomeIcon size="2x" icon={faCircleX} />
            </button>
          </div>

          <div className="heading">
            {image && (
              <div className="image">
                {image.gatsbyImageData && (
                  <GatsbyImage
                    image={image.gatsbyImageData}
                    alt={image.title}
                    className="image__item"
                  />
                )}
                {image.svg && (
                  <div
                    style={{ width: imageWidth, height: imageHeight }}
                    className="service-thumbnail__icon"
                    dangerouslySetInnerHTML={{
                      __html: image.svg.originalContent,
                    }}
                  />
                )}
              </div>
            )}
            <div className="title">
              <Text noRhythem type="body1" tag="span">
                {title}
              </Text>
            </div>
            {rightSide && <div className="right-side">{clonedRightSide}</div>}
          </div>
          <div className="content">
            <Text noRhythem color="white_1" type="body3" tag="p">
              {content}
            </Text>
          </div>
          {footer && <div className="footer">{footer}</div>}
        </div>
      </StyledSimpleModal>
    </Modal>
  );
};
